import { Injectable } from '@angular/core';
import { CLAMP_PATTERNS, COLUMN_DISPLAY_CONFIG, ColumnDisplay, ColumnDisplayEntry, NOWRAP_PATTERNS } from './column-display.config';
const BOX_CLASS = /(^|\s)(bg-|rounded|border|shadow|p-\d|px-|py-|badge|chip)/;
const INLINE_TAGS = new Set(['A', 'SPAN', 'B', 'I', 'EM', 'STRONG', 'SMALL', 'U', 'SUP', 'SUB', 'CODE', 'MAT-ICON', 'APP-STATUS-DOT']);
const TEXT_CELL_TAGS = new Set([...INLINE_TAGS, 'IMG', 'BR']);
const NUMERIC_TEXT = /^[\d.,%\s()+-]+$/;
const DATE_TEXT = /^(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|[A-Z][a-z]{2,8}\.? \d{1,2},? \d{4}|\d{1,2} [A-Z][a-z]{2,8},? \d{4})\b/;

interface ClampJob {
  cell: HTMLElement;
  target: HTMLElement;
  forceFit: boolean;
}

interface GlueStep {
  el: HTMLElement;
  trim: Node | null;
  glue: boolean;
}

@Injectable({ providedIn: 'root' })
export class AutoCellTooltipService {
  private static readonly CELL_SELECTOR = 'th[class*="mat-column-"], td[class*="mat-column-"], td[data-auto-clamp], td[data-auto-empty]';

  private observer?: MutationObserver;

  private tooltipEl?: HTMLDivElement;
  private tooltipSurface?: HTMLDivElement;
  private hideTimer?: number;
  private currentCell: HTMLElement | null = null;
  private showTimer?: number;

  private frameChecks = new Map<HTMLElement, HTMLElement>();
  private delayedChecks = new Map<HTMLElement, HTMLElement>();

  init(): void {
    if (typeof document === 'undefined') return;

    this.createTooltipElement();
    this.processAll(document.body);

    this.observer = new MutationObserver((mutations) => {
      const cells = new Set<HTMLElement>();
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          this.collectCells(node as HTMLElement, cells);
        }
        if (mutation.type === 'characterData') {
          const parent = mutation.target.parentElement;
          if (parent) {
            const cell = parent.closest(AutoCellTooltipService.CELL_SELECTOR) as HTMLElement | null;
            if (cell) cells.add(cell);
          }
        }
      }
      this.processCells(cells);
    });

    this.observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    document.addEventListener('mouseover', this.onMouseOver, true);
    document.addEventListener('mouseout', this.onMouseOut, true);
    window.addEventListener('scroll', this.hideTooltip, true);
    window.addEventListener('resize', this.hideTooltip);
  }

  // ─── Tooltip element ──────────────────────────────────────────────────────

  private createTooltipElement(): void {
    const el = document.createElement('div');
    el.className = 'mdc-tooltip mat-mdc-tooltip';
    el.setAttribute('role', 'tooltip');
    el.style.cssText = [
      'position:fixed',
      'z-index:99999',
      'pointer-events:none',
      'opacity:0',
      'transform:translateY(6px)',
      'transition:opacity 200ms cubic-bezier(0,0,0.2,1),transform 200ms cubic-bezier(0,0,0.2,1)',
      'max-width:420px',
      'left:-9999px',
      'top:-9999px',
    ].join(';');

    const surface = document.createElement('div');
    surface.className = 'mdc-tooltip__surface mdc-tooltip__surface-animation mat-mdc-tooltip-surface';
    surface.style.cssText = ['transform:none !important', 'max-width:420px', 'word-break:break-word', 'border-radius:8px', 'padding:6px 10px'].join(';');

    el.appendChild(surface);
    document.body.appendChild(el);

    this.tooltipEl = el;
    this.tooltipSurface = surface;
  }

  private onMouseOver = (event: Event): void => {
    const target = (event as MouseEvent).target as HTMLElement | null;
    if (!target) return;
    const td = target.closest('td') as HTMLElement | null;
    const box = td?.querySelector('[data-clamp]') as HTMLElement | null;
    if (td && box) this.updateClampTooltip(td, box);
    const cell = target.closest('[data-auto-tooltip]') as HTMLElement | null;
    if (!cell || cell === this.currentCell) return;
    const text = cell.getAttribute('data-auto-tooltip');
    if (!text) return;
    this.currentCell = cell;
    clearTimeout(this.showTimer);
    this.showTimer = window.setTimeout(() => this.showTooltip(cell, text), 150);
  };

  private onMouseOut = (event: Event): void => {
    const cell = this.currentCell;
    if (!cell) return;
    const next = (event as MouseEvent).relatedTarget as Element | null;
    if (next && cell.contains(next)) return;
    this.currentCell = null;
    clearTimeout(this.showTimer);
    this.hideTooltip();
  };

  private showTooltip(cell: HTMLElement, text: string): void {
    const el = this.tooltipEl!;
    const surface = this.tooltipSurface!;

    surface.textContent = text;

    el.style.left = '-9999px';
    el.style.top = '-9999px';
    el.classList.add('mdc-tooltip--shown');

    requestAnimationFrame(() => {
      if (this.currentCell !== cell) return;
      const innerTarget = cell.querySelector('[data-clamp]') as HTMLElement | null;
      const anchor = innerTarget ?? cell;
      const anchorRect = anchor.getBoundingClientRect();
      const tipRect = el.getBoundingClientRect();

      let top = anchorRect.top - tipRect.height - 8;
      let left = anchorRect.left + anchorRect.width / 2 - tipRect.width / 2;

      if (top < 8) top = anchorRect.bottom + 8;
      left = Math.max(8, Math.min(left, window.innerWidth - tipRect.width - 8));
      top = Math.max(8, Math.min(top, window.innerHeight - tipRect.height - 8));

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;

      requestAnimationFrame(() => {
        if (this.currentCell !== cell) return;
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      });
    });
  }

  private hideTooltip = (): void => {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    this.currentCell = null;

    if (!this.tooltipEl) return;

    this.tooltipEl.style.opacity = '0';
    this.tooltipEl.style.transform = 'translateY(6px)';
    this.tooltipEl.classList.remove('mdc-tooltip--shown');

    this.hideTimer = window.setTimeout(() => {
      if (this.tooltipEl) {
        this.tooltipEl.style.left = '-9999px';
        this.tooltipEl.style.top = '-9999px';
      }
    }, 160);
  };

  private processAll(root: ParentNode): void {
    this.processCells(Array.from(root.querySelectorAll<HTMLElement>(AutoCellTooltipService.CELL_SELECTOR)));
  }

  private collectCells(el: HTMLElement, target: Set<HTMLElement>): void {
    if (el.matches?.(AutoCellTooltipService.CELL_SELECTOR)) target.add(el);
    el.querySelectorAll?.<HTMLElement>(AutoCellTooltipService.CELL_SELECTOR).forEach((c) => target.add(c));
  }

  private processCells(cells: Iterable<HTMLElement>): void {
    const jobs: ClampJob[] = [];
    for (const cell of cells) {
      if (cell.closest('[data-no-auto-clamp]')) continue;

      const display = this.resolveDisplay(cell);
      if (!display) continue;

      const entries: ColumnDisplayEntry[] = Array.isArray(display) ? display : [display];
      for (const entry of entries) {
        if (entry === 'nowrap') this.applyNowrap(cell);
        else if (entry === 'empty-only') this.applyEmptyOnly(cell);
        else {
          const job = this.prepareClamp(cell, entry.clamp, entry.width, entry.empty, entry.fit);
          if (job) jobs.push(job);
        }
      }
    }

    const measured = jobs.map((job) => ({ job, structural: this.isStructural(job), glue: this.measureGlue(job.target) }));
    measured.forEach(({ job, structural, glue }) => this.finishClamp(job, structural, glue));
  }

  private resolveDisplay(cell: HTMLElement): ColumnDisplay | null {
    if (cell.hasAttribute('data-auto-empty')) return 'empty-only';
    const explicitClamp = cell.getAttribute('data-auto-clamp');
    if (explicitClamp) {
      const n = parseInt(explicitClamp, 10);
      if (!isNaN(n) && n >= 1 && n <= 3) return { clamp: n as 1 | 2 | 3, width: parseInt(cell.getAttribute('data-auto-width') ?? '', 10) || undefined };
      return null;
    }

    const colClass = Array.from(cell.classList).find((c) => c.startsWith('mat-column-'));
    if (!colClass) return null;
    const colName = colClass.replace('mat-column-', '').replace(/_filter$/, '');

    const explicit = COLUMN_DISPLAY_CONFIG[colName];
    if (explicit) return explicit;
    if (NOWRAP_PATTERNS.some((p) => p.test(colName))) return 'nowrap';
    if (CLAMP_PATTERNS.some((p) => p.test(colName))) return { clamp: 2 };
    return this.defaultDisplay(cell);
  }

  private defaultDisplay(cell: HTMLElement): ColumnDisplay | null {
    if (cell.tagName !== 'TD') return null;
    const elements = Array.from(cell.querySelectorAll('*')) as HTMLElement[];
    if (elements.some((el) => !TEXT_CELL_TAGS.has(el.tagName) || BOX_CLASS.test(el.className))) return null;
    const text = (cell.textContent ?? '').trim();
    if (text && (NUMERIC_TEXT.test(text) || DATE_TEXT.test(text))) return 'nowrap';
    return { clamp: 2, empty: false, fit: true };
  }

  private applyEmptyOnly(cell: HTMLElement): void {
    if (cell.tagName !== 'TD') return;
    const rawText = (cell.textContent ?? '').trim();
    if (!rawText) {
      if (!cell.hasAttribute('data-empty')) cell.setAttribute('data-empty', '');
    } else {
      if (cell.hasAttribute('data-empty')) cell.removeAttribute('data-empty');
    }
  }

  private applyNowrap(cell: HTMLElement): void {
    if (!cell.hasAttribute('data-nowrap')) {
      cell.setAttribute('data-nowrap', '');
    }
    if (cell.hasAttribute('data-auto-tooltip')) {
      cell.removeAttribute('data-auto-tooltip');
    }
  }

  private prepareClamp(cell: HTMLElement, lines: number, width?: number, empty = true, forceFit = false): ClampJob | null {
    if (cell.tagName !== 'TD') return null;

    // Empty-cell detection — show "Not Available" via CSS, skip clamp/tooltip
    const rawText = (cell.textContent ?? '').trim();
    if (!rawText) {
      if (empty && !cell.hasAttribute('data-empty')) cell.setAttribute('data-empty', '');
      return null;
    }
    if (cell.hasAttribute('data-empty')) cell.removeAttribute('data-empty');

    const target = this.resolveClampTarget(cell);
    if (!target) return null;

    if (target.getAttribute('data-clamp') !== String(lines)) {
      target.setAttribute('data-clamp', String(lines));
    }

    if (width) cell.style.minWidth = `${width}px`;

    if (forceFit && rawText.length < 16) cell.removeAttribute('data-clamp-host');
    else if (cell.getAttribute('data-clamp-host') !== String(lines)) cell.setAttribute('data-clamp-host', String(lines));

    return { cell, target, forceFit };
  }

  private isStructural({ cell, target }: ClampJob): boolean {
    const parent = target.parentElement ?? cell;
    return /flex/.test(getComputedStyle(parent).display) || Array.from(parent.children).some((c) => c !== target && c.tagName !== 'MAT-MENU');
  }

  private measureGlue(target: HTMLElement): GlueStep[] {
    const steps: GlueStep[] = [];
    for (const el of Array.from(target.children) as HTMLElement[]) {
      const prev = el.previousSibling;
      const afterElement = prev?.nodeType === Node.ELEMENT_NODE && !el.hasAttribute('data-glued');
      const trim = prev?.nodeType === Node.TEXT_NODE && /\S\s+$/.test(prev.textContent ?? '') ? prev : null;
      if (afterElement || trim) steps.push({ el, trim, glue: getComputedStyle(el).marginLeft === '0px' });
    }
    return steps;
  }

  private finishClamp({ cell, target, forceFit }: ClampJob, structural: boolean, glue: GlueStep[]): void {
    for (const step of glue) {
      if (step.trim) step.trim.textContent = (step.trim.textContent ?? '').replace(/\s+$/, '');
      if (step.glue) step.el.setAttribute('data-glued', '');
    }

    const fit = structural ? '' : forceFit ? 'grow' : null;
    if (fit !== target.getAttribute('data-clamp-fit')) fit === null ? target.removeAttribute('data-clamp-fit') : target.setAttribute('data-clamp-fit', fit);

    this.scheduleOverflowCheck(cell, target);
  }

  private scheduleOverflowCheck(cell: HTMLElement, target: HTMLElement): void {
    if (!this.frameChecks.size) requestAnimationFrame(() => this.checkOverflow(this.frameChecks));
    if (!this.delayedChecks.size) setTimeout(() => this.checkOverflow(this.delayedChecks), 150);
    this.frameChecks.set(cell, target);
    this.delayedChecks.set(cell, target);
  }

  private checkOverflow(checks: Map<HTMLElement, HTMLElement>): void {
    const pending = Array.from(checks).filter(([, target]) => target.isConnected);
    checks.clear();
    const overflowing = pending.map(([, target]) => this.isOverflowing(target));
    pending.forEach(([cell, target], i) => this.applyClampTooltip(cell, target, overflowing[i]));
  }

  private isInlineContent(el: HTMLElement): boolean {
    return Array.from(el.childNodes).every((n) => n.nodeType !== Node.ELEMENT_NODE || INLINE_TAGS.has((n as HTMLElement).tagName));
  }

  private resolveClampTarget(cell: HTMLElement): HTMLElement | null {
    const existing = cell.querySelector('[data-clamp]') as HTMLElement | null;
    if (existing) return existing;
    const nodes = Array.from(cell.childNodes).filter((n) => n.nodeType === Node.ELEMENT_NODE || (n.nodeType === Node.TEXT_NODE && (n.textContent ?? '').trim()));
    const hasLooseText = nodes.some((n) => n.nodeType === Node.TEXT_NODE);
    const inlineOnly = nodes.every((n) => n.nodeType === Node.TEXT_NODE || INLINE_TAGS.has((n as HTMLElement).tagName));
    if (hasLooseText || !cell.firstElementChild || (nodes.length > 1 && inlineOnly)) return this.wrapTextInSpan(cell);
    const only = nodes.length === 1 ? (nodes[0] as HTMLElement) : null;
    if (only && (only.tagName === 'SPAN' || only.tagName === 'DIV') && only.children.length > 0 && this.isInlineContent(only)) {
      if (!only.getAttribute('style') && !BOX_CLASS.test(only.className)) only.style.setProperty('display', 'contents', 'important');
      return this.wrapTextInSpan(only);
    }
    return this.findPrimaryTextElement(cell);
  }

  private wrapTextInSpan(cell: HTMLElement): HTMLElement | null {
    const text = (cell.textContent ?? '').trim();
    if (!text) return null;
    const span = document.createElement('span');
    while (cell.firstChild) span.appendChild(cell.firstChild);
    cell.appendChild(span);
    return span;
  }

  private findPrimaryTextElement(cell: HTMLElement): HTMLElement | null {
    const holder = this.findTextHolder(cell);
    if (!holder) return null;
    if (holder.children.length === 0) return holder;
    const movable = Array.from(holder.childNodes).filter((n) => n.nodeType === Node.TEXT_NODE || INLINE_TAGS.has((n as HTMLElement).tagName));
    const span = document.createElement('span');
    holder.insertBefore(span, movable[0]);
    movable.forEach((n) => span.appendChild(n));
    return span;
  }

  private findTextHolder(el: HTMLElement): HTMLElement | null {
    for (const child of Array.from(el.children) as HTMLElement[]) {
      if (Array.from(child.childNodes).some((n) => n.nodeType === Node.TEXT_NODE && (n.textContent ?? '').trim())) return child;
      const found = this.findTextHolder(child);
      if (found) return found;
    }
    return null;
  }

  private updateClampTooltip(cell: HTMLElement, target: HTMLElement): void {
    this.applyClampTooltip(cell, target, this.isOverflowing(target));
  }

  private isOverflowing(target: HTMLElement): boolean {
    return target.scrollHeight > target.clientHeight + 1;
  }

  private applyClampTooltip(cell: HTMLElement, target: HTMLElement, isOverflowing: boolean): void {
    if (isOverflowing) {
      const text = (target.textContent ?? '').trim();
      if (text && cell.getAttribute('data-auto-tooltip') !== text) {
        cell.setAttribute('data-auto-tooltip', text);
      }
    } else if (cell.hasAttribute('data-auto-tooltip')) {
      cell.removeAttribute('data-auto-tooltip');
    }
    if (cell.hasAttribute('title')) cell.removeAttribute('title');
  }
}
